import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2, X, Upload, RefreshCcw, Bus } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { resolveImageUrl } from "../../lib/imageUrl";

const empty = () => ({
    name: "Yeni Transfer",
    vehicle_type: "Otobüs",
    capacity: 45,
    route: "",
    description: "",
    features: [],
    duration: "",
    price_per_person: 0,
    price_private: 0,
    image: "",
    images: [],
    active: true,
});

const AdminTransfers = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/transfers");
            setItems(data);
        } catch { toast.error("Transferler yüklenemedi"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const update = (i, field, value) => {
        const n = [...items];
        if (["capacity", "price_per_person", "price_private"].includes(field)) value = Number(value) || 0;
        n[i][field] = value;
        setItems(n);
    };

    const addNew = async () => {
        try {
            const { data } = await api.post("/admin/transfers", empty());
            setItems([data, ...items]);
            toast.success("Yeni transfer eklendi");
        } catch { toast.error("Eklenemedi"); }
    };

    const save = async (i) => {
        const t = items[i];
        setSaving((s) => ({ ...s, [t.id]: true }));
        try {
            await api.put(`/admin/transfers/${t.id}`, t);
            toast.success("Kaydedildi");
        } catch { toast.error("Kaydedilemedi"); }
        finally { setSaving((s) => ({ ...s, [t.id]: false })); }
    };

    const remove = async (i) => {
        const t = items[i];
        if (!window.confirm(`"${t.name}" silinsin mi?`)) return;
        try {
            await api.delete(`/admin/transfers/${t.id}`);
            setItems(items.filter((_, idx) => idx !== i));
            toast.success("Silindi");
        } catch { toast.error("Silinemedi"); }
    };

    const uploadImage = async (i, file, asMain = true) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const n = [...items];
            if (asMain) {
                n[i].image = data.url;
            } else {
                n[i].images = [...(n[i].images || []), data.url];
            }
            setItems(n);
            toast.success("Görsel yüklendi — kaydet'i unutmayın");
        } catch { toast.error("Görsel yüklenemedi"); }
    };

    const addFeature = (i, value) => {
        const v = String(value || "").trim();
        if (!v) return;
        const n = [...items];
        n[i].features = [...(n[i].features || []), v];
        setItems(n);
    };
    const removeFeature = (i, fi) => {
        const n = [...items];
        n[i].features.splice(fi, 1);
        setItems(n);
    };
    const removeImage = (i, gi) => {
        const n = [...items];
        n[i].images.splice(gi, 1);
        setItems(n);
    };

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Transfer & Otobüs</h1>
                    <p className="text-muted_green text-sm">Otobüs, VIP minibüs ve özel araç transfer hizmetleri.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="btn-outline-gold !text-xs" data-testid="refresh-transfers"><RefreshCcw className="w-3.5 h-3.5" /> Yenile</button>
                    <button onClick={addNew} className="btn-gold !text-xs" data-testid="add-transfer"><Plus className="w-3.5 h-3.5" /> Yeni Transfer</button>
                </div>
            </div>

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center">
                    <Bus className="w-12 h-12 text-muted_green mx-auto mb-3" />
                    <div className="text-muted_green text-sm">Henüz transfer eklenmemiş. &ldquo;Yeni Transfer&rdquo; ile başlayın.</div>
                </div>
            ) : (
                <div className="space-y-6">
                    {items.map((t, i) => (
                        <div key={t.id} className="border border-border_green bg-emerald_surface p-5" data-testid={`admin-transfer-${t.id}`}>
                            <div className="flex items-center justify-between mb-4 gap-2">
                                <input className="input-dark text-lg font-serif flex-1" value={t.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Transfer adı" data-testid={`name-${t.id}`} />
                                <label className="flex items-center gap-2 text-xs">
                                    <input type="checkbox" checked={!!t.active} onChange={(e) => update(i, "active", e.target.checked)} />
                                    <span className="text-muted_green">Aktif</span>
                                </label>
                                <button onClick={() => save(i)} disabled={saving[t.id]} className="btn-gold !text-xs" data-testid={`save-${t.id}`}><Save className="w-3.5 h-3.5" /> Kaydet</button>
                                <button onClick={() => remove(i)} className="text-rose-400 hover:text-rose-500 p-2" data-testid={`del-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-5">
                                {/* Left: image + gallery */}
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-gold mb-1">Ana Görsel</div>
                                    <div className="aspect-[16/10] bg-emerald_deep border border-border_green mb-2 overflow-hidden flex items-center justify-center">
                                        {t.image ? (
                                            <img src={resolveImageUrl(t.image)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <Bus className="w-12 h-12 text-muted_green" />
                                        )}
                                    </div>
                                    <label className="btn-outline-gold !text-xs w-full justify-center cursor-pointer">
                                        <Upload className="w-3.5 h-3.5" /> Ana Görsel Yükle
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(i, e.target.files?.[0], true)} data-testid={`upload-main-${t.id}`} />
                                    </label>

                                    {(t.images || []).length > 0 && (
                                        <div className="mt-3">
                                            <div className="text-[10px] uppercase tracking-[0.18em] text-gold mb-1">Galeri ({t.images.length})</div>
                                            <div className="grid grid-cols-4 gap-1.5">
                                                {(t.images || []).map((g, gi) => (
                                                    <div key={gi} className="relative aspect-square bg-emerald_deep border border-border_green overflow-hidden">
                                                        <img src={resolveImageUrl(g)} alt="" className="w-full h-full object-cover" />
                                                        <button onClick={() => removeImage(i, gi)} className="absolute top-0.5 right-0.5 bg-rose-600 text-white rounded-full p-0.5">
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <label className="btn-outline-gold !text-xs w-full justify-center cursor-pointer mt-2">
                                        + Galeri Görseli Ekle
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(i, e.target.files?.[0], false)} />
                                    </label>
                                </div>

                                {/* Right: fields */}
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="label-dark">Araç Tipi</label>
                                            <input className="input-dark" value={t.vehicle_type} onChange={(e) => update(i, "vehicle_type", e.target.value)} placeholder="Otobüs / Sprinter / Vito" data-testid={`vt-${t.id}`} />
                                        </div>
                                        <div>
                                            <label className="label-dark">Kapasite</label>
                                            <input type="number" className="input-dark" value={t.capacity} onChange={(e) => update(i, "capacity", e.target.value)} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label-dark">Rota</label>
                                        <input className="input-dark" value={t.route} onChange={(e) => update(i, "route", e.target.value)} placeholder="Mekke → Cidde Havalimanı" data-testid={`route-${t.id}`} />
                                    </div>
                                    <div>
                                        <label className="label-dark">Süre</label>
                                        <input className="input-dark" value={t.duration} onChange={(e) => update(i, "duration", e.target.value)} placeholder="~1 saat 15 dk" />
                                    </div>
                                    <div>
                                        <label className="label-dark">Açıklama / Rota İçeriği</label>
                                        <textarea className="input-dark" rows={4} value={t.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Klimalı, WiFi'lı, lüks koltuklu..." data-testid={`desc-${t.id}`} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="label-dark">Kişi Başı (SAR)</label>
                                            <input type="number" className="input-dark" value={t.price_per_person} onChange={(e) => update(i, "price_per_person", e.target.value)} data-testid={`pp-${t.id}`} />
                                        </div>
                                        <div>
                                            <label className="label-dark">Özel/Tam Araç (SAR)</label>
                                            <input type="number" className="input-dark" value={t.price_private} onChange={(e) => update(i, "price_private", e.target.value)} data-testid={`pr-${t.id}`} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label-dark">Özellikler</label>
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {(t.features || []).map((f, fi) => (
                                                <span key={fi} className="bg-emerald_deep/60 border border-border_green text-bone text-xs px-2 py-1 inline-flex items-center gap-1">
                                                    {f}
                                                    <button onClick={() => removeFeature(i, fi)} className="text-rose-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                                                </span>
                                            ))}
                                        </div>
                                        <input
                                            className="input-dark"
                                            placeholder="Özellik ekle ve Enter (örn: Klima, WiFi, USB Şarj)"
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(i, e.target.value); e.target.value = ""; } }}
                                            data-testid={`add-feature-${t.id}`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminTransfers;
